import { ReactNode } from 'react';
import { Card, Collapse, H4, H5 } from '@blueprintjs/core';
import { classNames } from '../../utils/classNames';
import { IconButton } from '../IconButton';
import styles from './AceCard.module.css';
import { BrandLogo } from '../BrandLogo/BrandLogo';
import { BrokerLogo } from '../BrokerLogo/BrokerLogo';
import { useAceCardStore } from './aceCardStore';

export function AceCardSection({
  id,
  title,
  tag,
  children,
  isExpandedByDefault = false,
  onCollapsed,
}: {
  id: string;
  title: ReactNode;
  tag?: ReactNode;
  children: ReactNode;
  isExpandedByDefault?: boolean;
  onCollapsed?: (a: boolean) => void;
}) {
  const {
    expanded,
    actions: { toggleExpanded },
  } = useAceCardStore(`AceCardSection.${id}`, isExpandedByDefault ?? false);

  const setCollapseControl = () => {
    toggleExpanded();

    if (onCollapsed) {
      onCollapsed(!expanded);
    }
  };

  return (
    <div className={classNames(styles.section)}>
      <div className={styles.header}>
        <IconButton
          icon={expanded ? 'chevron-up' : 'chevron-down'}
          onClick={setCollapseControl}
          classes={{ container: styles.wide }}
          type="button"
        >
          <div className={styles.title}>
            <H5 className={classNames(styles.noMargin, styles.sectionTitle)}>
              {title}
            </H5>
            {tag}
          </div>
        </IconButton>
      </div>
      <Collapse isOpen={expanded}>
        <div className={styles.sectionChildren}>{children}</div>
      </Collapse>
    </div>
  );
}

export interface IAceCard {
  id: string;
  title: string;
  leftIcon?: ReactNode;
  leftTag?: ReactNode;
  rightTag?: ReactNode;
  metaTags?: Array<Record<string, any>> | null;
  children: ReactNode;
  noBorder?: boolean;
  isExpandedByDefault?: boolean;
  noContentPadding?: boolean;
  classes?: {
    root?: string;
    content?: string;
  };
}

/**
 * @deprecated For the love of God, DO NOT USE THIS COMPONENT, YOU HAVE BEEN WARNED!
 */
export function AceCard({
  id,
  title,
  leftIcon = null,
  leftTag = null,
  rightTag = null,
  metaTags = null,
  children,
  noBorder = false,
  isExpandedByDefault = true,
  noContentPadding = false,
  classes,
}: IAceCard) {
  const key = `AceCard.${id}`;
  const {
    expanded,
    actions: { toggleExpanded },
  } = useAceCardStore(key, isExpandedByDefault);

  return (
    <Card
      id={key}
      className={classNames(
        noBorder ? styles.noBorder + ' ' + styles.noBorder : '',
        styles.card,
        classes?.root,
      )}
    >
      <div className={classNames(styles.header)}>
        <IconButton
          icon={expanded ? 'chevron-up' : 'chevron-down'}
          onClick={() => toggleExpanded()}
          classes={{ root: styles.padding, container: styles.wide }}
          type="button"
        >
          <div className={styles.title}>
            <div className={styles.leftTitle}>
              <div className={styles.leftIcon}>{leftIcon}</div>
              <H4 className={styles.noMargin}>{title}</H4>
              {leftTag && <div className={styles.leftTag}>{leftTag}</div>}
            </div>
            <div
              className={styles.rightTag}
              onClick={(e) => e.stopPropagation()}
            >
              {rightTag}
            </div>
            {metaTags ? (
              <div className={styles.rightTag}>
                {metaTags.map((x) => {
                  if (x.Brand !== undefined)
                    return (
                      <BrandLogo
                        brand={x.Brand}
                        fallback=""
                        className={styles.brand}
                        key={x.Brand}
                      />
                    );

                  if (x.Broker !== undefined)
                    return (
                      <BrokerLogo
                        broker={x.Broker}
                        fallback=""
                        key={x.Broker}
                      />
                    );

                  return null;
                })}
              </div>
            ) : null}
          </div>
        </IconButton>
      </div>
      <Collapse isOpen={expanded}>
        <div
          className={classNames(
            noContentPadding || !expanded ? '' : styles.padding,
            styles.collapse,
            classes?.content,
          )}
        >
          {children}
        </div>
      </Collapse>
    </Card>
  );
}
